import { __assign, __read } from 'tslib';
import { useEffect } from 'react';
import tabbable from 'tabbable';
import { getOwnerDocument } from '@reach/utils';

function getTopPosition(targetRect, popoverRect, isDirectionUp) {
    return {
        top: isDirectionUp
            ? "".concat(targetRect.top - popoverRect.height + window.pageYOffset, "px")
            : "".concat(targetRect.top + targetRect.height + window.pageYOffset, "px"),
    };
}
var positionDefault = function (targetRect, popoverRect) {
    if (!targetRect || !popoverRect) {
        return {};
    }
    var _a = getCollisions(targetRect, popoverRect), directionRight = _a.directionRight, directionUp = _a.directionUp;
    return __assign({ left: directionRight
            ? "".concat(targetRect.right - popoverRect.width + window.pageXOffset, "px")
            : "".concat(targetRect.left + window.pageXOffset, "px") }, getTopPosition(targetRect, popoverRect, directionUp));
};
var positionRight = function (targetRect, popoverRect) {
    if (!targetRect || !popoverRect) {
        return {};
    }
    var _a = getCollisions(targetRect, popoverRect), directionLeft = _a.directionLeft, directionUp = _a.directionUp;
    return __assign({ left: directionLeft
            ? "".concat(targetRect.left + window.pageXOffset, "px")
            : "".concat(targetRect.right - popoverRect.width + window.pageXOffset, "px") }, getTopPosition(targetRect, popoverRect, directionUp));
};
var positionMatchWidth = function (targetRect, popoverRect) {
    if (!targetRect || !popoverRect) {
        return {};
    }
    var directionUp = getCollisions(targetRect, popoverRect).directionUp;
    return __assign({ width: targetRect.width, left: targetRect.left }, getTopPosition(targetRect, popoverRect, directionUp));
};
var getScreenWidth = function () {
    return Math.min(window.innerWidth, window.screen.width, window.screen.availWidth);
};
var getScreenHeight = function () {
    return Math.min(window.innerHeight, window.screen.height, window.screen.availHeight);
};
function getCollisions(targetRect, popoverRect, offsetLeft, offsetBottom) {
    if (offsetLeft === void 0) { offsetLeft = 0; }
    if (offsetBottom === void 0) { offsetBottom = 0; }
    var collisions = {
        top: targetRect.top - popoverRect.height < 0,
        right: getScreenWidth() < targetRect.left + popoverRect.width - offsetLeft,
        bottom: getScreenHeight() < targetRect.bottom + popoverRect.height - offsetBottom,
        left: targetRect.left + targetRect.width - popoverRect.width < 0,
    };
    var directionRight = collisions.right && !collisions.left;
    var directionLeft = collisions.left && !collisions.right;
    var directionUp = collisions.bottom && !collisions.top;
    var directionDown = collisions.top && !collisions.bottom;
    return { directionRight: directionRight, directionLeft: directionLeft, directionUp: directionUp, directionDown: directionDown };
}
// Comments inherit from reachUI
// Heads up, my jQuery past haunts this function. This hook scopes the tab
// order to the React element tree, instead of the DOM tree. This way, when the
// user navigates with tab from the targetRef, the tab order moves into the
// popup, and then out of the popup back to the rest of the document.
// (We call targetRef, triggerRef inside this function to avoid confusion with
// event.target)
function useSimulateTabNavigationForReactTree(triggerRef, popoverRef) {
    var ownerDocument = getOwnerDocument(triggerRef.current);
    function handleKeyDown(event) {
        if (event.key === "Tab" &&
            popoverRef.current &&
            tabbable(popoverRef.current).length === 0) {
            return;
        }
        if (event.key === "Tab" && event.shiftKey) {
            if (shiftTabbedFromElementAfterTrigger(event)) {
                focusLastTabbableInPopover(event);
            }
            else if (shiftTabbedOutOfPopover(event)) {
                focusLastTabbableInPopover(event);
                focusTriggerRef(event);
            }
            else if (shiftTabbedToBrowserChrome(event)) {
                disableTabbablesInPopover();
            }
        }
        else if (event.key === "Tab") {
            if (tabbedFromTriggerToPopover()) {
                focusFirstPopoverTabbable(event);
            }
            else if (tabbedOutOfPopover()) {
                focusFirstPopoverTabbable(event);
                focusTriggerRef(event);
            }
            else if (tabbedToBrowserChrome(event)) {
                disableTabbablesInPopover();
            }
        }
    }
    useEffect(function () {
        ownerDocument === null || ownerDocument === void 0 ? void 0 : ownerDocument.addEventListener("keydown", handleKeyDown);
        return function () {
            ownerDocument === null || ownerDocument === void 0 ? void 0 : ownerDocument.removeEventListener("keydown", handleKeyDown);
        };
    }, []);
    function getElementAfterTrigger() {
        if (!ownerDocument)
            return;
        var elements = tabbable(ownerDocument);
        var targetIndex = elements && triggerRef.current
            ? elements.indexOf(triggerRef.current)
            : -1;
        var elementAfterTrigger = elements && elements[targetIndex + 1];
        return popoverRef.current &&
            popoverRef.current.contains(elementAfterTrigger || null)
            ? false
            : elementAfterTrigger;
    }
    function tabbedFromTriggerToPopover() {
        return triggerRef.current
            ? triggerRef.current === (ownerDocument === null || ownerDocument === void 0 ? void 0 : ownerDocument.activeElement)
            : false;
    }
    function focusFirstPopoverTabbable(event) {
        var elements = popoverRef.current && tabbable(popoverRef.current);
        if (elements && elements[0]) {
            event.preventDefault();
            elements[0].focus();
        }
    }
    function tabbedOutOfPopover() {
        var inPopover = popoverRef.current
            ? popoverRef.current.contains((ownerDocument === null || ownerDocument === void 0 ? void 0 : ownerDocument.activeElement) || null)
            : false;
        if (inPopover) {
            var elements = popoverRef.current && tabbable(popoverRef.current);
            return Boolean(elements &&
                elements[elements.length - 1] === (ownerDocument === null || ownerDocument === void 0 ? void 0 : ownerDocument.activeElement));
        }
        return false;
    }
    function shiftTabbedFromElementAfterTrigger(event) {
        if (!event.shiftKey)
            return;
        var elementAfterTrigger = getElementAfterTrigger();
        return event.target === elementAfterTrigger;
    }
    function focusLastTabbableInPopover(event) {
        var elements = popoverRef.current && tabbable(popoverRef.current);
        var last = elements && elements[elements.length - 1];
        if (last) {
            event.preventDefault();
            last.focus();
        }
    }
    function shiftTabbedOutOfPopover(event) {
        var elements = popoverRef.current && tabbable(popoverRef.current);
        if (elements) {
            return elements.length === 0 ? false : event.target === elements[0];
        }
        return false;
    }
    function focusTriggerRef(event) {
        var _a;
        event.preventDefault();
        (_a = triggerRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    }
    function tabbedToBrowserChrome(event) {
        if (!ownerDocument)
            return false;
        var elements = popoverRef.current
            ? tabbable(ownerDocument).filter(function (element) { var _a; return !((_a = popoverRef.current) === null || _a === void 0 ? void 0 : _a.contains(element)); })
            : null;
        return elements ? event.target === elements[elements.length - 1] : false;
    }
    function shiftTabbedToBrowserChrome(event) {
        if (!ownerDocument)
            return false;
        // we're assuming the popover will never contain the first tabbable
        // element, and it better not, because the trigger needs to be tabbable!
        return event.target === tabbable(ownerDocument)[0];
    }
    var restoreTabIndexTuplés = [];
    function disableTabbablesInPopover() {
        var elements = popoverRef.current && tabbable(popoverRef.current);
        if (elements) {
            elements.forEach(function (element) {
                restoreTabIndexTuplés.push([element, element.tabIndex]);
                element.tabIndex = -1;
            });
            ownerDocument === null || ownerDocument === void 0 ? void 0 : ownerDocument.addEventListener("focusin", enableTabbablesInPopover);
        }
    }
    function enableTabbablesInPopover() {
        ownerDocument === null || ownerDocument === void 0 ? void 0 : ownerDocument.removeEventListener("focusin", enableTabbablesInPopover);
        restoreTabIndexTuplés.forEach(function (_a) {
            var _b = __read(_a, 2), element = _b[0], tabIndex = _b[1];
            element.tabIndex = tabIndex;
        });
    }
}

export { getCollisions, positionDefault, positionMatchWidth, positionRight, useSimulateTabNavigationForReactTree };
//# sourceMappingURL=usePopover.js.map
